import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Card, Col, Dropdown, Offcanvas, Row, Toast, ToastContainer } from 'react-bootstrap';

import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'components/base/DatePicker';
import {
    faFileExport,
    faFilter,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import Dropzone from 'components/base/Dropzone';


interface FileItem {
    file_id: number;
    file_name: string;
    meta: string;
    inserted_at: string;
}

const FilesTable = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};


const FileManager = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('FileManager.page_title'),
            active: true
        }
    ];

    const ActionDropdownItem = ({ file_id, onDelete }: {
        file_id: string | number,
        onDelete: (id: string | number) => void;
    }) => {
        return (
            <>
                <Dropdown.Item eventKey="2" onClick={() => onDelete(file_id)} className="text-danger">
                    Remove
                </Dropdown.Item>
            </>
        );
    };

    const auth = useAuth();

    const handleClick = () => {
        setFiles([]);
        setShow(true);
    };

    const [loading, setLoading] = useState(true);

    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    // drawer
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);

    // for table
    const [fileItems, setFileItems] = useState<FileItem[]>([]);

    // x dropzone
    const [files, setFiles] = useState<File[]>([]);

    // State to control Toast messages
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const fileListTableColumns: ColumnDef<FileItem>[] = [
        {
            header: 'Filename',
            accessorKey: 'file_name',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '50%' }, className: 'ps-3' }
            }
        },

        {
            header: 'Meta',
            accessorKey: 'meta',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '40%' }, className: 'ps-3' }
            }
        },
        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '10%' }, className: 'ps-3' }
            }
        },
        {
            id: 'filesDropdown',
            cell: ({ row: { original } }) => {
                return (
                    <RevealDropdownTrigger>
                        <RevealDropdown>
                            <ActionDropdownItem file_id={original.file_id} onDelete={deleteFile} />
                        </RevealDropdown>
                    </RevealDropdownTrigger>
                );
            },
            meta: {
                cellProps: {
                    className: 'text-end pe-0 ps-4'
                }
            }
        }
    ];

    const table = useAdvanceTable({
        data: fileItems,
        columns: fileListTableColumns,
        pageSize: 6,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/admin/files',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            setFileItems(response.data);
        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShowToast(true);
                return
            }

        } finally {
            setLoading(false);
        }
    };

    const handleSumbit = async () => {
        const form = new FormData();
        // new file(s)
        files.forEach(file => {
            form.append('files', file);
        });

        try {
            const response = await apiClient.post(
                '/v1/admin/files', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            setToastMessage(response.data['message'])
            setShowToast(true);
            setShow(false);
            fetchData();

        } catch (error) {
            console.error('Request failed:', error);
        }

    };
    const deleteFile = async (id: string | number) => {
        if (window.confirm("Are you sure you want to delete this item?")) {
            const response = await apiClient.delete(
                `/v1/admin/files/${id}`,
                {
                    headers: {
                        'accept': 'application/json',

                    }
                }
            );
            setToastMessage(response.data['message'])
            setShowToast(true);
            fetchData();
        }
    };


    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <>

            <PageBreadcrumb items={breadcrumbItems} />

            <Offcanvas show={show} onHide={handleClose} backdrop="static" placement="end" style={{ 'width': '35%' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className='fs-5' >Add files</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="mb-5">
                        <Dropzone single={false}
                            className="mb-3"
                            onFileListUpdate={files => setFiles(files)}
                            accept={{
                                //'image/*': ['.png', '.gif', '.jpeg', '.jpg'],
                                'application/pdf': ['.pdf'],
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
                            }}
                        />
                    </div>

                    <Button
                        variant="primary"
                        onClick={handleSumbit}
                    >
                        Save
                    </Button>


                </Offcanvas.Body>
            </Offcanvas>

            <ToastContainer className="p-3" position='top-center'>
                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
                    <Toast.Body className=''>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>

            <Card className='mt-3  mb-3 border border-light rounded-5' >
                <Card.Body>
                    <AdvanceTableProvider {...table}>

                        <Row className="g-3 justify-content-between mb-4">
                            <Col xs="auto">
                                <Button
                                    variant="primary"
                                    className="me-4"
                                    startIcon={
                                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                    }
                                    onClick={handleClick}
                                >
                                    {t('FileManager.add_file')}
                                </Button>

                            </Col>
                            <Col xs="auto">
                                <div className="d-flex">
                                    <SearchBox
                                        placeholder="Search by name"
                                        className="me-2"
                                        onChange={handleSearchInputChange}
                                    />
                                    <DatePicker defaultValue="Jan 1, 2025" />
                                    <Button
                                        variant="phoenix-secondary"
                                        className="px-3 ms-2"
                                        onClick={() => setOpenFilterModal(true)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFilter}
                                            transform="down-3"
                                            className="text-primary"
                                        />
                                    </Button>
                                </div>
                                <LeadsFilterModal
                                    show={openFilterModal}
                                    handleClose={() => setOpenFilterModal(false)}
                                />
                            </Col>
                        </Row>
                        <FilesTable />
                    </AdvanceTableProvider>
                </Card.Body>
            </Card>
        </>
    );


};

export default FileManager;
